import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'gatsby'
import { setColorTheme, getEventLink, getConferenceLocation } from 'theme/utils/generic-helpers'
import { getCurrentStageSessions } from 'src/utils/session-helpers'
import Countdown from 'src/components/countdown.js'
import { isEqual } from 'lodash-es'

const ConferenceLiveSection = ({ conference, sessions }) => {
  const [stages, setStages] = useState([])
  const conferenceLocation = getConferenceLocation(conference)
  const partners = conference.data.Partners
  const stageIds = []
  const unsortedStages = []
  let workshops = []

  const [stageSessions, setStageSessions] = useState({})
  const [currentWorkshops, setCurrentWorkshops] = useState({})
  const [availableOnDemand, setAvailableOnDemand] = useState([])

  const getCurrentWorkshops = (workshops) => {
    const current = []
    const next = []
    workshops.forEach((workshop) => {
      if (next.length === 0) next.push(workshop)
      if (
        next.length > 0 &&
        new Date(workshop.data.UTC_End_Time).getTime() === new Date(next[0].data.UTC_End_Time).getTime()
      )
        next.push(workshop)
      if (
        new Date(workshop.data.UTC_Start_Time).getTime() < Date.now() &&
        new Date(workshop.data.UTC_End_Time).getTime() > Date.now()
      )
        current.push(workshop)
    })
    return {
      current,
      next,
    }
  }

  const getAvailableOnDemand = (sessions) => {
    return sessions.filter((session) => {
      // has a video and the starttime is in the past
      return session.data.Video && new Date(session.data.UTC_Start_Time).getTime() < Date.now()
    })
  }

  useEffect(() => {
    const tempWorkshops = []
    sessions.forEach((session) => {
      if (
        session.data.Stage &&
        session.data.Stage[0] &&
        stageIds.indexOf(session.data.Stage[0].data.Stage_Slug) === -1 &&
        session.data.Session_Groups?.[0]?.data?.Location?.indexOf('Online') !== -1
      ) {
        stageIds.push(session.data.Stage[0].data.Stage_Slug)
        unsortedStages.push(session.data.Stage[0])
      }
      const format = session.data.Session_Groups?.[0]?.data?.Format ?? null
      if (format === 'Workshop') {
        tempWorkshops.push(session)
      }
    })
    const stages = unsortedStages.sort((a, b) => {
      return a.data.Stage_Priority - b.data.Stage_Priority
    })
    setStages(stages)
    const tempStageSessions = getCurrentStageSessions(sessions, stages)
    setStageSessions(tempStageSessions)

    workshops = tempWorkshops.sort((a, b) => {
      return new Date(a.data.UTC_Start_Time).getTime() - new Date(b.data.UTC_Start_Time).getTime()
    })
    const tempCurrentWorkshops = getCurrentWorkshops(workshops)
    setCurrentWorkshops(tempCurrentWorkshops)

    const tempAvailableOnDemand = getAvailableOnDemand(sessions)
    setAvailableOnDemand(tempAvailableOnDemand)

    const timer = setInterval(() => {
      const tempStageSessions = getCurrentStageSessions(sessions, stages)
      if (!isEqual(stageSessions, tempStageSessions)) {
        setStageSessions(tempStageSessions)
      }

      const tempCurrentWorkshops = getCurrentWorkshops(workshops)
      if (!isEqual(currentWorkshops, tempCurrentWorkshops)) {
        setCurrentWorkshops(tempCurrentWorkshops)
      }

      const tempAvailableOnDemand = getAvailableOnDemand(sessions)
      if (!isEqual(availableOnDemand, tempAvailableOnDemand)) {
        setAvailableOnDemand(tempAvailableOnDemand)
      }
    }, 1000 * 30)
    return () => clearInterval(timer)
  }, [])

  return (
    // List of stages
    <div className="bg-white py-16 w-full">
      <div className="mx-auto max-w-md px-4 text-center sm:max-w-3xl sm:px-6 lg:px-8 lg:max-w-7xl">
        <h1
          className={`${setColorTheme(
            'text-emea-600 text-apac-600 text-usa-600',
            conferenceLocation,
          )} text-2xl font-semibold tracking-wider uppercase`}
        >
          {conference.data.Conference_Name} Live
        </h1>
        <p className="mt-2 text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">
          Explore {conference.data.Conference_Name}
        </p>
        <p className="mt-5 max-w-prose mx-auto text-xl text-gray-500">
          {/* See what is happening on the stages, explore the expo or meet fellow attendees. */}
          Check out the live stages, network with attendees, access extra on-demand content and develop your
          skills in our workshops!
        </p>
        <div className="mt-12">
          <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-3">
            {stages.map((stage, index) => {
              const stageUrl = getEventLink(
                conference.data.Conference_Id,
                conferenceLocation,
                'live/stage/' + stage.data.Stage_Slug,
              )

              return (
                <div key={stage.id} className="pt-6">
                  <div className="flow-root bg-slate-100 rounded-lg px-6 pb-8 h-full">
                    <div className="flex flex-col justify-between h-full">
                      <div className="-mt-6">
                        <Link
                          to={stageUrl}
                          className={`${setColorTheme(
                            'bg-emea-200 bg-apac-200 bg-usa-200 bg-community-200',
                            conferenceLocation,
                          )} inline-flex items-center justify-center py-3 px-5 text-2xl text-white rounded-md shadow-lg`}
                        >
                          {/* {index + 1} */}
                          {index % 2 === 0 && (
                            <svg
                              className={`w-6 h-6 ${setColorTheme(
                                'text-emea-700 text-apac-700 text-usa-700 text-community-700',
                                conferenceLocation,
                              )}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
                              />
                            </svg>
                          )}
                          {index % 2 === 1 && (
                            <svg
                              className={`w-6 h-6 ${setColorTheme(
                                'text-emea-700 text-apac-700 text-usa-700 text-community-700',
                                conferenceLocation,
                              )}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 13v-1m4 1v-3m4 3V8M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
                              />
                            </svg>
                          )}
                        </Link>
                        <h3 className="mt-8 text-3xl font-medium text-gray-900 tracking-tight">
                          {stage.data.Stage_Name}
                        </h3>
                        <div className="mt-5 text-base text-gray-500 h-full">
                          {stageSessions &&
                            stageSessions[stage.data.Stage_Slug] &&
                            stageSessions[stage.data.Stage_Slug].currentSession && (
                              <>
                                <h4 className="text-xl pb-4 text-red">Live now</h4>
                                <Link to={stageUrl} className="text-lg font-medium line-clamp-3">
                                  {stageSessions[stage.data.Stage_Slug].currentSession.data.Talk_Title}
                                </Link>
                              </>
                            )}
                          {stageSessions &&
                            stageSessions[stage.data.Stage_Slug] &&
                            !stageSessions[stage.data.Stage_Slug].currentSession &&
                            stageSessions[stage.data.Stage_Slug].nextSession && (
                              <>
                                Starting in
                                <Countdown
                                  startTime={
                                    stageSessions[stage.data.Stage_Slug].nextSession.data.UTC_Start_Time
                                  }
                                  inline
                                />
                                <div className="mb-4 flex flex-col justify-between">
                                  <div className="py-4">
                                    <Link
                                      to={stageUrl}
                                      key={stageSessions[stage.data.Stage_Slug].nextSession.id}
                                      className="text-lg font-medium line-clamp-3"
                                    >
                                      {stageSessions[stage.data.Stage_Slug].nextSession.data.Talk_Title}
                                    </Link>
                                  </div>
                                </div>
                              </>
                            )}
                          {stageSessions &&
                            stageSessions[stage.data.Stage_Slug] &&
                            !stageSessions[stage.data.Stage_Slug].currentSession &&
                            !stageSessions[stage.data.Stage_Slug].nextSession && (
                              <>
                                <h4 className="text-xl pb-4">That&apos;s a wrap.</h4>
                                <p className="text-lg font-medium line-clamp-3">See you next year</p>
                              </>
                            )}
                        </div>
                      </div>
                      <div className="w-full px-4 pt-2">
                        <Link
                          to={stageUrl}
                          className={`${setColorTheme(
                            'bg-emea-600 hover:bg-emea-700 focus:ring-emea-500 bg-apac-600 hover:bg-apac-700 focus:ring-apac-500 bg-usa-600 hover:bg-usa-700 focus:ring-usa-500 bg-community-600 hover:bg-community-700 focus:ring-community-500',
                            conferenceLocation,
                          )} inline-flex items-center mt-4 px-5 py-2 border border-transparent text-base font-medium rounded-full shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2`}
                        >
                          Go to {stage.data.Stage_Name}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}

            {/* {workshops.length > 0 && (
              <div className='pt-6'>
                <div className='flow-root bg-slate-100 rounded-lg px-6 pb-8 h-full'>
                  <div className='flex flex-col justify-between h-full'>
                    <div className='-mt-6'>
                      <Link
                        to={
                          getEventLink(
                            conference.data.Conference_Id,
                            conferenceLocation,
                            'live/workshops'
                          ) 
                        }
                        className={`${setColorTheme(
                          'bg-emea-100 bg-apac-100 bg-usa-100 bg-community-100',
                          conferenceLocation
                        )} inline-flex items-center justify-center py-3 px-5 text-2xl text-white rounded-md shadow-lg`}
                      >
                        <svg
                          className={`w-6 h-6 ${setColorTheme(
                            'text-emea-600 text-apac-600 text-usa-600 text-community-600',
                            conferenceLocation
                          )}`}
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                          xmlns='http://www.w3.org/2000/svg'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z'
                          />
                        </svg>
                      </Link>
                      <h3 className='mt-8 text-2xl font-medium text-gray-900 tracking-tight'>
                        Workshops
                      </h3>
                      <div className='mt-5 text-base text-gray-500 h-full'>
                        {currentWorkshops.current &&
                          currentWorkshops.current.length > 0 && (
                            <>
                              <h4 className='text-xl pb-4'>Live now</h4>
                              {currentWorkshops.current.map(workshop => (
                                <Link
                                  key={workshop.data.Session_Slug}
                                  to={'/session/' + workshop.data.Session_Slug}
                                  className='text-lg font-medium line-clamp-3'
                                >
                                  {workshop.data.Talk_Title}
                                </Link>
                              ))}
                            </>
                          )}
                        {currentWorkshops.current &&
                          currentWorkshops.current.length === 0 &&
                          currentWorkshops.next &&
                          currentWorkshops.next > 0 && (
                            <>
                              Starting in
                              <Countdown
                                startTime={
                                  currentWorkshops.next[0].data.UTC_Start_Time
                                }
                                inline
                              />
                              <div className='mb-4 flex flex-col justify-between'>
                                <div className='py-4'>
                                  {currentWorkshops.next.map(workshop => (
                                    <Link
                                      key={workshop.data.Session_Slug}
                                      to={
                                        '/session/' + workshop.data.Session_Slug
                                      }
                                      className='text-lg font-medium line-clamp-3'
                                    >
                                      {workshop.data.Talk_Title}
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            </>
                          )}
                        {(!currentWorkshops.current ||
                          currentWorkshops.current.length === 0 ||
                          !currentWorkshops.next ||
                          currentWorkshops.current.length === 0) && (
                          <>
                            <h4 className='text-xl pb-4'>
                              That&apos;s a wrap.
                            </h4>
                            <p className='text-lg font-medium line-clamp-3'>
                              See you next year
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                    <div className='w-full px-4 pt-2'>
                      <Link
                        to={
                          getEventLink(
                            conference.data.Conference_Id,
                            conferenceLocation,
                            'live/workshops'
                          ) 
                        }
                        className={`${setColorTheme(
                          'bg-emea-500 hover:bg-emea-600 focus:ring-emea-400 bg-apac-500 hover:bg-apac-600 focus:ring-apac-400 bg-usa-500 hover:bg-usa-600 focus:ring-usa-400 bg-community-500 hover:bg-community-600 focus:ring-community-400',
                          conferenceLocation
                        )} inline-flex items-center mt-4 px-5 py-2 border border-transparent text-base font-medium rounded-full shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2`}
                      >
                        Go to Workshops
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )} */}

            <div className="pt-6">
              <div className="flow-root bg-slate-100 rounded-lg px-6 pb-8 h-full">
                <div className="flex flex-col justify-between h-full">
                  <div className="-mt-6">
                    <Link
                      to={getEventLink(conference.data.Conference_Id, conferenceLocation, 'live/agenda')}
                      // className={`${setColorTheme('bg-uxdx-500', conferenceLocation)} inline-flex items-center justify-center py-3 px-5 text-2xl text-white rounded-md shadow-lg`}
                    >
                      <span className="bg-teal-200 inline-flex items-center justify-center py-3 px-5 rounded-md shadow-lg">
                        <svg
                          className="w-6 h-6 text-teal-700"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                          />
                        </svg>
                      </span>
                    </Link>
                    <h3 className="mt-8 text-2xl font-medium text-gray-900 tracking-tight">Agenda</h3>
                    <p className="mt-5 text-base text-gray-500">
                      Explore the content across the conference and plan out your favourite sessions.
                    </p>
                    {/* {availableOnDemand.length > 0 && (
                      <Link
                        to={getEventLink(
                          conference.data.Conference_Id,
                          conferenceLocation,
                          'live/ondemand'
                        )}
                        className='text-lg text-gray-500 pt-4 font-medium line-clamp-3'
                      >
                        {availableOnDemand.length} talks now available
                        <br />
                        on-demand
                      </Link>
                    )} */}
                  </div>
                  <div className="w-full px-4 pt-2">
                    <Link
                      to={getEventLink(conference.data.Conference_Id, conferenceLocation, 'live/agenda')}
                      className="bg-teal-600 hover:bg-teal-700 focus:ring-teal-500 inline-flex items-center mt-4 px-5 py-2 border border-transparent text-base font-medium rounded-full shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
                    >
                      Go to Agenda
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {partners && partners.length > 0 && (
              <div className="pt-6">
                <div className="flow-root bg-slate-100 rounded-lg px-6 pb-8 h-full">
                  <div className="flex flex-col justify-between h-full">
                    <div className="-mt-6">
                      <Link
                        to={getEventLink(conference.data.Conference_Id, conferenceLocation, 'live/expo')}
                        className="bg-sky-200 inline-flex items-center justify-center py-3 px-5 text-2xl text-white rounded-md shadow-lg"
                      >
                        <svg
                          className="w-6 h-6 text-sky-700"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"
                          />
                        </svg>
                      </Link>
                      <h3 className="mt-8 text-2xl font-medium text-gray-900 tracking-tight">Expo</h3>
                      <p className="mt-5 text-base text-gray-500">
                        Meet our specially selected partners to help your product or your career.
                      </p>
                      {/* TODO: add some logos */}
                    </div>
                    <div className="w-full px-4 pt-2">
                      <Link
                        to={getEventLink(conference.data.Conference_Id, conferenceLocation, 'live/expo')}
                        className="bg-sky-600 hover:bg-sky-700 focus:ring-sky-500 inline-flex items-center mt-4 px-5 py-2 border border-transparent text-base font-medium rounded-full shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
                      >
                        Go to Expo
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* <div className='pt-6'>
              <div className='flow-root bg-slate-100 rounded-lg px-6 pb-8 h-full'>
                <div className='flex flex-col justify-between h-full'>
                  <div className='-mt-6'>
                    <Link
                      to={
                        getEventLink(
                          conference.data.Conference_Id,
                          conferenceLocation,
                          'live/networking'
                        )
                      }
                      // className={`${setColorTheme('bg-uxdx-500', conferenceLocation)} inline-flex items-center justify-center py-3 px-5 text-2xl text-white rounded-md shadow-lg`}
                    >
                      <span
                        className={`${setColorTheme(
                          'bg-uxdx-200',
                          conferenceLocation
                        )} inline-flex items-center justify-center py-3 px-5 rounded-md shadow-lg`}
                      >
                        <svg
                          className='w-6 h-6 text-indigo-700'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                          xmlns='http://www.w3.org/2000/svg'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
                          />
                        </svg>
                      </span>
                    </Link>
                    <h3 className='mt-8 text-2xl font-medium text-gray-900 tracking-tight'>
                      Networking
                    </h3>
                    <p className='mt-5 text-base text-gray-500'>
                      Explore our networking options to help you connect with
                      your fellow attendees.
                    </p>
                  </div>
                  <div className='w-full px-4 pt-2'>
                    <Link
                      to={
                        getEventLink(
                          conference.data.Conference_Id,
                          conferenceLocation,
                          'live/networking'
                        )
                      }
                      className={`${setColorTheme(
                        'bg-uxdx-600 hover:bg-uxdx-700 focus:ring-uxdx-500',
                        conferenceLocation
                      )} inline-flex items-center mt-4 px-5 py-2 border border-transparent text-base font-medium rounded-full shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2`}
                    >
                      Go to Networking
                    </Link>
                  </div>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </div>

    // list of networking

    // expo section
  )
}

export default ConferenceLiveSection
